import React, { useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, TextField } from '@mui/material';

import { translate as t } from 'hooks/useTranslations';
import { createAccreditationBody } from '../../_data_sources/AccreditationBodies/manageAccreditationBodies';
import { type ServerErrorResponse } from 'data_sources/constants';
import Modal from 'components/_layouts/Modal';
import errorHelper from 'helpers/errorHelper';
import { AccreditationBody } from '../../_types/awb';

export type NewAccreditingBodyProps = {
  setLastId: React.Dispatch<React.SetStateAction<string | null>>;
};

const NewAccreditingBody: React.FC<NewAccreditingBodyProps> = ({ setLastId }: NewAccreditingBodyProps) => {
  const queryClient = useQueryClient();
  const [serverErrors, setServerErrors] = useState<ServerErrorResponse>();
  const [isSaving, setIsSaving] = useState(false);
  const [showNewModal, setShowNewModal] = useState(false);

  const accreditingBodychema = Yup.object().shape({
    name: Yup.string()
      .trim()
      .transform((value: string, originalValue: string) => {
        return value.replace(/\s+/g, ' ');
      })
      .required(`${t('awb.accreditingBodies.properties.name')} is required`),
    abbreviation: Yup.string()
      .trim()
      .transform((value: string, originalValue: string) => {
        return value.replace(/\s+/g, ' ');
      })
      .required(`${t('awb.accreditingBodies.properties.abbreviation')} is required`),
  });

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    reset,
    trigger,
    watch,
  } = useForm({
    defaultValues: {
      name: '',
      abbreviation: '',
    },
    resolver: yupResolver(accreditingBodychema),
  });

  const handleSave: SubmitHandler<AccreditationBody> = (values: AccreditationBody) => {
    setIsSaving(true);
    createAccreditationBody(
      values,
      (data: AccreditationBody) => {
        reset();
        setServerErrors(undefined);
        queryClient.invalidateQueries(['accrediting_bodies']);
        if (data.id) setLastId(data.id);

        setIsSaving(false);
        setShowNewModal(false);
      },
      (errors: ServerErrorResponse) => {
        setIsSaving(false);
        setServerErrors(errors);
      }
    );
  };

  return (
    <>
      <Button
        onClick={() => setShowNewModal(true)}
        size='small'
      >
        {t('awb.accreditingBodies.actions.create')}
      </Button>
      <Modal
        title={t('awb.accreditingBodies.new.title')}
        onSubmit={handleSubmit(handleSave)}
        size='md'
        actions={
          <>
            <Button
              color='neutral'
              onClick={() => setShowNewModal(false)}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={() => {
                trigger();
              }}
              disabled={isSaving}
              component='button'
              type='submit'
              variant='contained'
            >
              {t('common.save')}
            </Button>
          </>
        }
        open={showNewModal}
        onClose={() => setShowNewModal(false)}
      >
        {errorHelper.renderAllErrors(serverErrors, errors)}
        <TextField
          id='name'
          key='name'
          label={t('awb.accreditingBodies.properties.name')}
          variant='outlined'
          required
          fullWidth
          {...register('name')}
          error={!!errors.name}
          helperText={errors.name ? errors.name.message : undefined}
        />
        <TextField
          id='abbreviation'
          key='abbreviation'
          label={t('awb.accreditingBodies.properties.abbreviation')}
          variant='outlined'
          required
          fullWidth
          {...register('abbreviation')}
          error={!!errors.abbreviation}
          helperText={errors.abbreviation ? errors.abbreviation.message : undefined}
        />
      </Modal>
    </>
  );
};

export default NewAccreditingBody;
